"use client";
/**
 * SANATAN DECODE — गोत्र, Episode 1.  v17 "THE MAP"
 *
 * First full-length episode (9:04). Built to the round-10 brief in §8b of the motion skill:
 *   ⭐ v14's descent tree kept — but drawn as an ORGANIC MAP, never a flow chart
 *   ⛔ the cast is never deleted to make room for the diagram (rishi + individual + couple all stay)
 *   ⛔ ONE bold move per beat; everything else settles
 *   ⛔ the GROUND IS STATIC — it travels only across a cut (overrides the kit's WALK entirely)
 *   ⚡ colour lives IN the ground (per-section tint), never a flat plane behind cutouts
 *
 * Timing is not hand-authored: gotraTiming.ts is generated from a forced alignment of the real
 * narration mp3, so every phrase lands on the word actually being spoken.
 */
import React from "react";
import { AbsoluteFill, Audio, Img, staticFile, interpolate, Easing, random, useVideoConfig, useCurrentFrame, spring } from "remotion";
import { deva, INK, GOLD, KUMKUM, MUTED_C, CREAM, Grain, PROP_SHADOW } from "./sanatan/kit";
import { SEG, PH } from "./sanatan/gotraTiming";


/* ══ FPS-AGNOSTIC TIMING ═══════════════════════════════════════
   ⛔ The kit hard-codes 30fps (`f = s*30`, `useStep` = every 2nd frame). This episode also renders
   at 16fps, where both are wrong — F() would place every cue at ~1.9× its real time. These read
   fps off the composition instead, so ONE component is correct at any frame rate.
   ⚡ LAW 1 (animate on twos) is about landing motion near 12-15fps, not about the literal number 2:
   at 30fps that means step 2 (=15fps), at 16fps it means step 1 (=16fps). Quantising 16fps "on
   twos" would give 8fps, which is judder, not Vox. */
const useSec = () => { const { fps } = useVideoConfig(); return (s: number) => s * fps; };
const useStepF = () => {
  const { fps } = useVideoConfig();
  const step = Math.max(1, Math.round(fps / 15));
  return Math.floor(useCurrentFrame() / step) * step;
};
const useSpringAtF = (at: number, damping = 9, stiffness = 110, mass = 1.1) => {
  const { fps } = useVideoConfig();
  const frame = useStepF();
  return spring({ frame: frame - at * fps, fps, config: { damping, stiffness, mass } });
};

const cut = (n: string) => staticFile(`sanatan/cut/${n}.png`);
const pho = (n: string) => staticFile(`sanatan/photo/${n}.png`);
const PANOS = ["gurukul_pano", "pano_sabha", "modern_pano", "pano_courtroom"];

/* ══ BEAT TABLE ════════════════════════════════════════════════
   segs: inclusive narration-segment range · g: which ground · x: where it is parked (0..1)
   tint: the colour that lives IN the ground for this section (LAW: never a flat plane) */
type BeatDef = { segs: [number, number]; g: number; x: number; tint?: string; op?: number };
const B: BeatDef[] = [
  { segs: [0, 1],   g: 0, x: 0.00 },                              // hook — the six occasions
  { segs: [2, 2],   g: 0, x: 0.08 },                              // setup — three words
  { segs: [3, 4],   g: 0, x: 0.18 },                              // two systems
  { segs: [5, 6],   g: 0, x: 0.30 },                              // ⭐ the rishi chain (v09 keeper)
  { segs: [7, 8],   g: 1, x: 0.00, tint: "#C9A24B", op: 0.20 },   // laukik — village register world
  { segs: [9, 12],  g: 1, x: 0.14 },                              // three clarifiers
  { segs: [13, 15], g: 0, x: 0.42 },                              // baudhayana — seven + agastya
  { segs: [16, 17], g: 0, x: 0.54 },                              // the counts explode
  { segs: [18, 18], g: 0, x: 0.64 },                              // shakha
  { segs: [19, 23], g: 1, x: 0.32, tint: "#C9A24B", op: 0.20 },   // the real list, Kataria
  { segs: [24, 26], g: 1, x: 0.48, tint: "#C9A24B", op: 0.20 },   // no rishi column
  { segs: [27, 30], g: 0, x: 0.74 },                              // प्रवर + भीष्म
  { segs: [31, 32], g: 0, x: 0.86 },                              // why 49
  { segs: [33, 34], g: 0, x: 0.96 },                              // सपिंड
  { segs: [35, 36], g: 0, x: 0.84 },                              // sankalpa (havan returns)
  { segs: [37, 39], g: 0, x: 0.70 },                              // marriage — three tests
  { segs: [40, 41], g: 0, x: 0.58 },                              // Kane's four gotras
  { segs: [42, 43], g: 2, x: 0.00, tint: "#7FA6B8", op: 0.18 },   // science opens — modern ground
  { segs: [44, 46], g: 0, x: 0.46 },                              // शास्त्र side — Charak
  { segs: [47, 49], g: 2, x: 0.26, tint: "#7FA6B8", op: 0.18 },   // ⭐ science side (v09 DNA beat)
  { segs: [50, 50], g: 2, x: 0.44, tint: "#7FA6B8", op: 0.18 },   // Y chromosome
  { segs: [51, 52], g: 3, x: 0.10, tint: "#8C9C7A", op: 0.22 },   // the law — 1946
  { segs: [53, 55], g: 0, x: 0.34 },                              // close — what to answer
  { segs: [56, 57], g: 0, x: 0.22 },                              // सत्यकाम (the rishi returns)
  { segs: [58, 61], g: 0, x: 0.10 },                              // open question + CTA + sign-off
];
const bt = (i: number): [number, number] => [SEG[B[i].segs[0]].start, SEG[B[i].segs[1]].end];

/* ══ GROUND ════════════════════════════════════════════════════
   ⛔ §8b rule 6: held perfectly still while a beat is resident, travelling ONLY across the cut.
   No reclaim drift, no vertical breathe, no per-beat parallax — those all read as "it keeps moving". */
const PANO_W = 3840, PANO_H = 1280, DISPLAY_H = 980;
const Ground: React.FC = () => {
  const frame = useStepF();
  const F = useSec();
  const dispW = (PANO_W / PANO_H) * DISPLAY_H;
  const travel = dispW - 1280;

  const ts: number[] = [], xs: number[] = [];
  B.forEach((b, i) => {
    const [a, z] = bt(i);
    ts.push(F(a), F(z));            // flat hold across the whole beat …
    xs.push(b.x, b.x);              // … then the gap to the next beat IS the travel
  });
  // ⚠️ forced-aligned word bounds can overlap by a few ms where two segments join, which makes
  //    the keyframe list non-monotonic and throws inside interpolate(). Nudge, never reorder.
  for (let i = 1; i < ts.length; i++) if (ts[i] <= ts[i - 1]) ts[i] = ts[i - 1] + 1;
  const x = interpolate(frame, ts, xs, {
    extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.inOut(Easing.cubic),
  });

  // which plate + which tint: a hard swap at the transition, never a dissolve
  let gi = 0, tint = "", top = 0;
  for (let i = 0; i < B.length; i++) {
    if (frame >= F(bt(i)[0]) - 8) { gi = B[i].g; tint = B[i].tint ?? ""; top = B[i].op ?? 0; }
  }
  return (
    <AbsoluteFill style={{ backgroundColor: "#F2E6CC", overflow: "hidden", zIndex: 0 }}>
      <Img src={staticFile(`sanatan/pano/${PANOS[gi]}.png`)} style={{
        position: "absolute", height: DISPLAY_H, width: dispW,
        left: -x * travel, top: -150, maxWidth: "none",
        filter: "blur(4.2px) saturate(0.62) brightness(1.12) contrast(0.84)",
      }} />
      <AbsoluteFill style={{ backgroundColor: "rgba(249,240,222,0.34)" }} />
      {tint && <AbsoluteFill style={{ backgroundColor: tint, opacity: top, mixBlendMode: "multiply" }} />}
      <AbsoluteFill style={{ background: "radial-gradient(ellipse 84% 78% at 50% 46%, rgba(255,252,242,0.30) 0%, rgba(198,158,100,0.34) 100%)" }} />
    </AbsoluteFill>
  );
};

/* ══ TYPE — anchored to the real spoken word ═══════════════════ */
const Phrase: React.FC<{
  k: string; size?: number; color?: string; x?: number; y?: number;
  hold?: number; align?: "left" | "center"; width?: number;
}> = ({ k, size = 62, color = INK, x = 64, y = 92, hold = 2.6, align = "left", width = 760 }) => {
  const p = PH[k];
  const sIn = useSpringAtF(p ? p[0] : 1e6, 9, 150, 0.8);
  const sOut = useSpringAtF(p ? p[1] + hold : 1e6, 200, 80, 0.5);
  if (!p) return null;
  return (
    <div style={{
      position: "absolute", left: x, top: y, width, textAlign: align,
      fontFamily: deva, fontWeight: 800, fontSize: size, lineHeight: 1.3, color,
      textShadow: "0 0 26px rgba(255,251,240,0.98), 0 0 10px rgba(255,251,240,0.95), 0 2px 2px rgba(255,251,240,0.85)",
      opacity: Math.min(1, sIn * 1.5) * (1 - Math.min(1, sOut * 1.4)),
      transform: `translateY(${(1 - sIn) * 34 + sOut * 44}px) rotate(${5 * (1 - sIn)}deg) scale(${0.9 + sIn * 0.1})`,
      transformOrigin: "0% 140%",
    }}>{p[2]}</div>
  );
};

const Sub: React.FC<{ text: string; at: number; out: number; x?: number; y?: number; size?: number; color?: string }> =
  ({ text, at, out, x = 64, y = 200, size = 30, color = MUTED_C }) => {
    const sIn = useSpringAtF(at, 9, 160, 0.8);
    const sOut = useSpringAtF(out, 200, 80, 0.5);
    return (
      <div style={{
        position: "absolute", left: x, top: y, maxWidth: 640,
        fontFamily: deva, fontWeight: 700, fontSize: size, lineHeight: 1.4, color,
        textShadow: "0 0 22px rgba(255,251,240,0.98), 0 0 9px rgba(255,251,240,0.95)",
        opacity: Math.min(1, sIn * 1.5) * (1 - Math.min(1, sOut * 1.4)),
        transform: `translateY(${(1 - sIn) * 20 + sOut * 40}px)`,
      }}>{text}</div>
    );
  };

/* ══ PROP — swings in, idles on a pendulum, swings out ═════════ */
const P: React.FC<{
  src: string; h: number; left: number; top: number; inAt: number; outAt: number;
  from?: "left" | "right" | "top"; amp?: number; z?: number;
}> = ({ src, h, left, top, inAt, outAt, from = "left", amp = 15, z = 12 }) => {
  const frame = useStepF();
  const F = useSec();
  const sIn = useSpringAtF(inAt, 8, 95, 1.15);
  const sOut = useSpringAtF(outAt, 200, 75, 0.5);
  const sign = from === "right" ? -1 : 1;
  const pend = Math.sin((frame - F(inAt)) / 34) * 1.35 * sIn * (1 - sOut);
  const trav = 250;
  const dx = from === "top" ? 0 : trav * sign * (1 - sIn) - trav * 1.5 * sign * sOut;
  const dy = from === "top" ? -trav * (1 - sIn) + trav * 2 * sOut : 70 * (1 - sIn) + 190 * sOut;
  if (frame < F(inAt) - 30 || frame > F(outAt) + 40) return null;
  return (
    <Img src={src} style={{
      position: "absolute", left, top, height: h, zIndex: z,
      filter: PROP_SHADOW,
      transform: `translate(${dx}px, ${dy}px) rotate(${amp * sign * (1 - sIn) + pend - 28 * sign * sOut}deg) scale(${0.9 + 0.1 * sIn - 0.12 * sOut})`,
      transformOrigin: "50% -28%",
      opacity: Math.min(1, sIn * 1.6) * (1 - Math.min(1, sOut * 1.4)),
    }} />
  );
};

/* ══ THE MAP ═══════════════════════════════════════════════════
   v14's win, redrawn ORGANIC. Node positions are pure functions of (gen, index) with a
   deterministic wobble, so the same node can be asked for at any scale in any beat with no
   stored state — and it reads as a drawn map, not a grid of boxes on connector curves. */
const mapNode = (gen: number, i: number, n: number, w: number, h: number) => {
  const jx = (random(`x${gen}-${i}`) - 0.5) * (w / (n + 1)) * 0.55;
  const jy = (random(`y${gen}-${i}`) - 0.5) * 34;
  return { x: (w * (i + 1)) / (n + 1) + jx, y: 40 + gen * (h / 5.4) + jy };
};

const DescentMap: React.FC<{ gens: number; lit?: number[]; w?: number; h?: number; accent?: string }> =
  ({ gens, lit = [], w = 620, h = 430, accent = KUMKUM }) => {
    const frame = useStepF();
    const paths: React.ReactNode[] = [], dots: React.ReactNode[] = [];
    for (let g = 0; g < gens; g++) {
      const n = Math.min(2 ** g, 12);
      for (let i = 0; i < n; i++) {
        const p = mapNode(g, i, n, w, h);
        if (g > 0) {
          const pn = Math.min(2 ** (g - 1), 12);
          const q = mapNode(g - 1, Math.floor(i / 2) % pn, pn, w, h);
          const mx = (p.x + q.x) / 2 + (random(`c${g}-${i}`) - 0.5) * 26;
          paths.push(<path key={`p${g}-${i}`} d={`M ${q.x} ${q.y} Q ${mx} ${(p.y + q.y) / 2} ${p.x} ${p.y}`}
            fill="none" stroke={INK} strokeWidth={1.7} strokeLinecap="round" opacity={0.42} />);
        }
        const on = g === gens - 1 && lit.includes(i);
        dots.push(<circle key={`d${g}-${i}`} cx={p.x} cy={p.y} r={on ? 9 : 5.2}
          fill={on ? accent : CREAM} stroke={on ? accent : INK} strokeWidth={on ? 2 : 1.6} opacity={on ? 1 : 0.9} />);
      }
    }
    const draw = interpolate(frame % 10000, [0, 1], [1, 1]);   // static once built
    return (
      <svg width={w} height={h} style={{ overflow: "visible", opacity: draw }}>
        {paths}{dots}
      </svg>
    );
  };

/* ══ BEAT GATE ═════════════════════════════════════════════════ */
const Beat: React.FC<{ i: number; children: React.ReactNode }> = ({ i, children }) => {
  const frame = useStepF();
  const F = useSec();
  const [a, z] = bt(i);
  if (frame < F(a - 0.6) || frame >= F(z + 0.8)) return null;
  return <AbsoluteFill style={{ zIndex: 10 }}>{children}</AbsoluteFill>;
};

/* ══ THE DYNAMIC FACTOR ════════════════════════════════════════
   Every beat below is authored ONCE in 1280×720 coordinates. This wrapper scales that stage to
   whatever the registered composition size is, so the same component ships 480p (cheap proof) and
   720p (ship) with no second layout and no re-tuning of a single left/top value.
   ⛔ Do NOT use `--scale` for this: 1280×720 × 2/3 is 853.33×480, and Remotion rejects a
      non-integer height ("must be an integer, but is 480.0024"). The factor belongs in the DOM. */
const AUTHOR_W = 1280, AUTHOR_H = 720;
const Stage: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { width, height } = useVideoConfig();
  const k = Math.max(width / AUTHOR_W, height / AUTHOR_H);   // cover, never letterbox
  return (
    <AbsoluteFill style={{ backgroundColor: "#F2E6CC", overflow: "hidden" }}>
      <div style={{
        position: "absolute", width: AUTHOR_W, height: AUTHOR_H,
        left: (width - AUTHOR_W * k) / 2, top: (height - AUTHOR_H * k) / 2,
        transform: `scale(${k})`, transformOrigin: "0 0",
      }}>{children}</div>
    </AbsoluteFill>
  );
};

/* ══ THE FILM ══════════════════════════════════════════════════ */
export const SanatanDecodeGotraV17: React.FC = () => (
  <Stage><Film /></Stage>
);

const Film: React.FC = () => {
  const frame = useStepF();
  const F = useSec();

  // ── persistent objects (LAW 3: ONE instance, transformed — never re-introduced) ──
  // the rishi opens the definition beat and RETURNS for सत्यकाम at the close: same png, moved.
  const rishiWin: [number, number][] = [[bt(3)[0], 0], [bt(3)[0] + 0.7, 1], [bt(3)[1], 1], [bt(3)[1] + 0.8, 0],
                                        [bt(23)[0], 0], [bt(23)[0] + 0.7, 1], [bt(23)[1], 1], [bt(23)[1] + 0.8, 0]];
  const rishiOn = interpolate(frame, rishiWin.map((k) => F(k[0])), rishiWin.map((k) => k[1]),
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const rishiL = interpolate(frame, [F(bt(3)[0]), F(bt(3)[1]), F(bt(23)[0])], [700, 700, 120],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.inOut(Easing.cubic) });
  const rishiH = interpolate(frame, [F(bt(3)[0]), F(bt(3)[1]), F(bt(23)[0])], [430, 430, 360],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.inOut(Easing.cubic) });

  return (
    <AbsoluteFill style={{ backgroundColor: "#F2E6CC" }}>
      <Audio src={staticFile("sanatan/audio/gotra_ep01.mp3")} />
      <Ground />

      {/* ── persistent rishi (stage level, outside every Beat) ── */}
      {rishiOn > 0.02 && (
        <Img src={cut("rishi")} style={{
          position: "absolute", left: rishiL, top: 250, height: rishiH, zIndex: 11,
          filter: PROP_SHADOW, opacity: rishiOn,
          transform: `rotate(${Math.sin(frame / 40) * 1.1}deg) scale(${0.92 + rishiOn * 0.08})`,
          transformOrigin: "50% -28%",
        }} />
      )}

      {/* ══ 0 · HOOK — the six occasions arrive one after another ══ */}
      <Beat i={0}>
        <P src={cut("rt_havan")}     h={210} left={80}  top={330} inAt={bt(0)[0] + 0.2} outAt={bt(0)[1] - 0.2} />
        <P src={cut("rt_razor")}     h={170} left={330} top={370} inAt={bt(0)[0] + 1.0} outAt={bt(0)[1] - 0.2} from="top" />
        <P src={cut("rt_mandap")}    h={215} left={545} top={330} inAt={bt(0)[0] + 1.9} outAt={bt(0)[1] - 0.2} />
        <P src={cut("rt_threshold")} h={165} left={800} top={375} inAt={bt(0)[0] + 2.8} outAt={bt(0)[1] - 0.2} from="right" />
        <P src={cut("rt_pot")}       h={170} left={1020} top={370} inAt={bt(0)[0] + 3.9} outAt={bt(0)[1] - 0.2} from="right" />
        <P src={cut("pp_pandit")}    h={330} left={840} top={210} inAt={PH.hook[0] - 1.2} outAt={bt(0)[1] - 0.1} from="right" z={14} />
        <Phrase k="hook" size={72} color={KUMKUM} x={70} y={80} width={700} />
      </Beat>

      {/* ══ 1 · SETUP — the three words, as the map's first seed ══ */}
      <Beat i={1}>
        <P src={cut("pp_phoneman")} h={360} left={950} top={250} inAt={bt(1)[0]} outAt={bt(1)[1] - 0.2} from="right" />
        <Phrase k="setup" size={66} x={70} y={90} width={640} />
        <Sub text="गोत्र · प्रवर · सपिंड" at={PH.setup[0] + 0.6} out={bt(1)[1]} x={70} y={210} size={40} color={GOLD} />
      </Beat>

      {/* ══ 2 · TWO SYSTEMS ══ */}
      <Beat i={2}>
        <div style={{ position: "absolute", left: 700, top: 150 }}><DescentMap gens={3} /></div>
        <Phrase k="two" size={64} x={70} y={100} width={560} />
        <Sub text="आर्ष — रिषि से" at={PH.arsha[0]} out={bt(2)[1]} x={70} y={250} size={34} color={KUMKUM} />
        <Sub text="लौकिक — लोक से" at={PH.arsha[0] + 1.4} out={bt(2)[1]} x={70} y={300} size={34} color={GOLD} />
      </Beat>

      {/* ══ 3 · ⭐ THE RISHI CHAIN (v09 keeper — the line walks back to one name) ══ */}
      <Beat i={3}>
        <P src={cut("pp_fatherson")} h={330} left={120} top={330} inAt={bt(3)[0] + 0.3} outAt={bt(3)[1] - 0.3} />
        <Phrase k="define" size={58} x={70} y={90} width={620} />
        <Sub text="पिता → पिता → पिता →" at={PH.define[0] + 1.0} out={bt(3)[1]} x={70} y={250} size={36} color={MUTED_C} />
      </Beat>

      {/* ══ 4 · LAUKIK ══ */}
      <Beat i={4}>
        <P src={cut("pp_genealogist")} h={400} left={840} top={230} inAt={bt(4)[0] + 0.2} outAt={bt(4)[1] - 0.2} from="right" />
        <Phrase k="laukik" size={70} color={GOLD} x={70} y={100} width={560} />
        <Sub text="पुरखा · गाँव · काम · वंश" at={PH.laukik[0] + 1.6} out={bt(4)[1]} x={70} y={230} size={34} />
      </Beat>

      {/* ══ 5 · THREE CLARIFIERS ══ */}
      <Beat i={5}>
        <P src={cut("modern_man")} h={380} left={880} top={250} inAt={bt(5)[0] + 0.3} outAt={bt(5)[1] - 0.3} from="right" />
        <Phrase k="surname" size={62} color={KUMKUM} x={70} y={110} width={600} />
        <Phrase k="caste" size={54} x={70} y={250} width={600} />
      </Beat>

      {/* ══ 6 · BAUDHAYANA — seven and the eighth ══ */}
      <Beat i={6}>
        <P src={cut("vd_sage_seated")} h={330} left={120} top={340} inAt={bt(6)[0] + 0.2} outAt={bt(6)[1] - 0.3} />
        <P src={cut("vd_sage_staff")}  h={370} left={430} top={310} inAt={bt(6)[0] + 1.1} outAt={bt(6)[1] - 0.3} from="top" />
        <div style={{ position: "absolute", left: 740, top: 170 }}><DescentMap gens={2} /></div>
        <Phrase k="seven" size={72} x={70} y={90} width={520} />
        <Phrase k="agastya" size={52} color={GOLD} x={70} y={210} width={520} />
      </Beat>

      {/* ══ 7 · THE COUNTS EXPLODE — one bold move ══ */}
      <Beat i={7}>
        <div style={{ position: "absolute", left: 560, top: 130 }}><DescentMap gens={5} w={680} h={470} /></div>
        <Phrase k="counts" size={78} color={KUMKUM} x={70} y={200} width={460} />
      </Beat>

      {/* ══ 8 · SHAKHA ══ */}
      <Beat i={8}>
        <div style={{ position: "absolute", left: 620, top: 160 }}><DescentMap gens={4} lit={[2, 5]} /></div>
        <Phrase k="shakha" size={56} x={70} y={150} width={520} />
      </Beat>

      {/* ══ 9 · THE REAL LIST — the register is EVIDENCE (photoreal) ══ */}
      <Beat i={9}>
        <P src={pho("rc_register")} h={420} left={700} top={230} inAt={bt(9)[0] + 0.4} outAt={bt(9)[1] - 0.3} from="right" z={13} />
        <Phrase k="columns" size={44} x={64} y={100} width={600} />
        <Phrase k="kataria" size={62} color={KUMKUM} x={64} y={300} width={560} />
        <Phrase k="purkha" size={44} color={GOLD} x={64} y={430} width={560} />
      </Beat>

      {/* ══ 10 · NO RISHI COLUMN ══ */}
      <Beat i={10}>
        <P src={cut("cl_fort")}   h={210} left={120} top={400} inAt={bt(10)[0] + 0.8} outAt={bt(10)[1] - 0.3} />
        <P src={cut("cl_shrine")} h={230} left={420} top={380} inAt={bt(10)[0] + 1.6} outAt={bt(10)[1] - 0.3} from="top" />
        <P src={pho("rc_register")} h={360} left={760} top={260} inAt={bt(10)[0]} outAt={bt(10)[1] - 0.3} from="right" z={13} />
        <Sub text="यह दूसरी तरह की सूची है" at={bt(10)[0] + 1.2} out={bt(10)[1]} x={70} y={200} size={46} color={INK} />
      </Beat>

      {/* ══ 11 · प्रवर + भीष्म ══ */}
      <Beat i={11}>
        <P src={cut("pp_pandit")}        h={340} left={110} top={330} inAt={bt(11)[0] + 0.3} outAt={bt(11)[1] - 0.3} />
        <P src={cut("vd_manuscript_open")} h={240} left={760} top={330} inAt={PH.bhishma[0] - 1.5} outAt={bt(11)[1] - 0.3} from="right" />
        <Phrase k="pravar" size={76} color={KUMKUM} x={70} y={90} width={520} />
        <Phrase k="bhishma" size={44} color={GOLD} x={70} y={470} width={640} />
      </Beat>

      {/* ══ 12 · WHY FORTY-NINE ══ */}
      <Beat i={12}>
        <P src={cut("vd_yajna")} h={300} left={820} top={310} inAt={bt(12)[0] + 0.2} outAt={bt(12)[1] - 0.3} from="right" />
        <Phrase k="fortynine" size={58} x={70} y={110} width={640} />
        <Phrase k="diff" size={46} color={KUMKUM} x={70} y={300} width={620} />
      </Beat>

      {/* ══ 13 · सपिंड — the couple (cast never deleted) ══ */}
      <Beat i={13}>
        <P src={cut("couple")} h={340} left={760} top={300} inAt={bt(13)[0] + 0.3} outAt={bt(13)[1] - 0.3} from="right" />
        <Phrase k="sapind" size={58} x={70} y={120} width={620} />
        <Sub text="माँ की तरफ़ पाँच पीढ़ी" at={PH.sapind[0] + 2.2} out={bt(13)[1]} x={70} y={270} size={38} color={GOLD} />
      </Beat>

      {/* ══ 14 · SANKALPA — the SAME havan returns (object permanence) ══ */}
      <Beat i={14}>
        <P src={cut("rt_havan")} h={260} left={800} top={320} inAt={bt(14)[0] + 0.2} outAt={bt(14)[1] - 0.3} from="right" />
        <Phrase k="sankalpa" size={54} x={70} y={140} width={620} />
      </Beat>

      {/* ══ 15 · MARRIAGE — three tests ══ */}
      <Beat i={15}>
        <P src={cut("rt_mandap")} h={250} left={790} top={320} inAt={bt(15)[0] + 0.3} outAt={bt(15)[1] - 0.3} from="right" />
        <P src={cut("couple")}    h={280} left={1000} top={340} inAt={bt(15)[0] + 1.0} outAt={bt(15)[1] - 0.3} from="right" />
        <Phrase k="three" size={64} color={KUMKUM} x={70} y={150} width={600} />
      </Beat>

      {/* ══ 16 · KANE — four different gotras, one pravara ══ */}
      <Beat i={16}>
        <div style={{ position: "absolute", left: 640, top: 170 }}><DescentMap gens={3} lit={[0, 1, 2, 3]} /></div>
        <Phrase k="kane" size={52} x={70} y={140} width={540} />
      </Beat>

      {/* ══ 17 · SCIENCE OPENS ══ */}
      <Beat i={17}>
        <P src={cut("dna")}        h={380} left={840} top={250} inAt={bt(17)[0] + 0.4} outAt={bt(17)[1] - 0.3} from="right" />
        <P src={cut("modern_man")} h={330} left={140} top={330} inAt={bt(17)[0] + 0.2} outAt={bt(17)[1] - 0.3} />
        <Phrase k="dnaclaim" size={52} x={70} y={110} width={620} />
      </Beat>

      {/* ══ 18 · शास्त्र SIDE — Charak, photoreal manuscript as proof ══ */}
      <Beat i={18}>
        <P src={pho("ph_manuscript")} h={260} left={780} top={330} inAt={bt(18)[0] + 0.4} outAt={bt(18)[1] - 0.3} from="right" z={13} />
        <Phrase k="charak" size={56} color={GOLD} x={70} y={120} width={620} />
        <Phrase k="adharma" size={48} color={KUMKUM} x={70} y={300} width={640} />
      </Beat>

      {/* ══ 19 · ⭐ SCIENCE SIDE — the DNA/allele beat (v09 keeper) ══ */}
      <Beat i={19}>
        <P src={cut("sc_bars")}     h={280} left={760} top={300} inAt={PH.risk[0] - 1.4} outAt={bt(19)[1] - 0.3} from="right" />
        <P src={cut("sc_carriers")} h={280} left={1030} top={300} inAt={PH.maternal[0] - 1.0} outAt={bt(19)[1] - 0.3} from="right" />
        <Phrase k="risk" size={60} color={KUMKUM} x={70} y={110} width={620} />
        <Phrase k="maternal" size={44} x={70} y={290} width={640} />
        <Phrase k="tests" size={46} color={GOLD} x={70} y={440} width={620} />
      </Beat>

      {/* ══ 20 · Y CHROMOSOME ══ */}
      <Beat i={20}>
        <P src={cut("sc_ychrom")} h={330} left={860} top={280} inAt={bt(20)[0] + 0.2} outAt={bt(20)[1] - 0.3} from="right" />
        <Phrase k="ychrom" size={68} color={KUMKUM} x={70} y={190} width={560} />
      </Beat>

      {/* ══ 21 · THE LAW ══ */}
      <Beat i={21}>
        <P src={pho("rc_statute")} h={300} left={780} top={300} inAt={bt(21)[0] + 0.3} outAt={bt(21)[1] - 0.3} from="right" z={13} />
        <P src={pho("rc_files")}   h={240} left={1030} top={350} inAt={bt(21)[0] + 1.2} outAt={bt(21)[1] - 0.3} from="right" z={13} />
        <Phrase k="legal" size={54} color={KUMKUM} x={70} y={110} width={620} />
        <Phrase k="lokachar" size={48} color={GOLD} x={70} y={330} width={600} />
      </Beat>

      {/* ══ 22 · CLOSE — what to answer ══ */}
      <Beat i={22}>
        <P src={cut("pp_pandit")}   h={320} left={800} top={330} inAt={bt(22)[0] + 0.3} outAt={bt(22)[1] - 0.3} from="right" />
        <P src={cut("pp_phoneman")} h={330} left={1060} top={320} inAt={bt(22)[0] + 1.0} outAt={bt(22)[1] - 0.3} from="right" />
        <Phrase k="answer" size={72} color={KUMKUM} x={70} y={130} width={520} />
        <Phrase k="kashyap" size={52} color={GOLD} x={70} y={300} width={520} />
      </Beat>

      {/* ══ 23 · सत्यकाम — the rishi returns (persistent, at stage level) ══ */}
      <Beat i={23}>
        <P src={cut("pp_elder")} h={320} left={560} top={340} inAt={bt(23)[0] + 0.6} outAt={bt(23)[1] - 0.3} from="right" />
        <Phrase k="satyakam" size={74} color={KUMKUM} x={780} y={200} width={440} align="left" />
      </Beat>

      {/* ══ 24 · OPEN QUESTION + CTA + SIGN-OFF ══ */}
      <Beat i={24}>
        <Phrase k="openq" size={46} x={70} y={120} width={660} />
        <Phrase k="cta" size={54} color={GOLD} x={70} y={300} width={620} />
        <Phrase k="signoff" size={86} color={KUMKUM} x={70} y={440} width={700} />
      </Beat>

      <Grain op={0.13} />
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
